import { ajaxController } from "./controller/AjaxController";
import { PlantPositionStatusDTO } from "../DTOs/Telemetry/PlantPositionStatusDTO";
import { BlastPatternFenceDTO } from "../DTOs/Telemetry/BlastPatternFenceDTO";
import { DigBlockFenceDTO } from "../DTOs/Telemetry/DigBlockFenceDTO";
import { DrillBlastMapOverlayDTO } from "../DTOs/Telemetry/DrillBlastMapOverlayDTO";
import { DeviceMappingDTO } from "../DTOs/Telemetry/DeviceMappingDTO";
import { PagedResult } from "../DTOs/Common/PagedResult";
import { ErrorResponse } from "../DTOs/Common/ErrorResponse";

export class TelemetryAjax {
  private _ajaxController = ajaxController;
  private _telemetryDataEndpoint = "/Telemetry";

  public getCurrentPositions() {
    return this._ajaxController.get<PlantPositionStatusDTO[]>(
      `${this._telemetryDataEndpoint}/telemetry/positions/current`,
      {}
    );
  }

  // Note: served by TelemetryController rather than BlastsController, so it sits behind the map's own
  // module gate. Only live (not completed) patterns that actually have a polygon come back.
  public getLiveBlastPatternFences() {
    return this._ajaxController.get<BlastPatternFenceDTO[]>(
      `${this._telemetryDataEndpoint}/telemetry/blast-pattern-fences`,
      {}
    );
  }

  public getDrillBlastOverlay() {
    return this._ajaxController.get<DrillBlastMapOverlayDTO>(`${this._telemetryDataEndpoint}/telemetry/drill-blast-overlay`, {});
  }

  public getLiveDigBlockFences() {
    return this._ajaxController.get<DigBlockFenceDTO[]>(
      `${this._telemetryDataEndpoint}/telemetry/dig-block-fences`,
      {}
    );
  }

  // Note: the endpoint returns a bare list, not a PagedResult, because a site has tens of devices rather
  // than thousands. useDataTable requires a PagedResult, so the wrap happens here rather than by changing
  // an endpoint that already has callers - and requesting a page of a list nobody pages is a fiction the
  // server would have to maintain forever.
  public async getDeviceMappingsPaged(): Promise<PagedResult<DeviceMappingDTO> | ErrorResponse> {
    const res = await this._ajaxController.get<DeviceMappingDTO[]>(
      `${this._telemetryDataEndpoint}/telemetry/device-mappings`,
      {}
    );

    if (ErrorResponse.isErrorResponse(res)) return res;

    return new PagedResult<DeviceMappingDTO>(res, res.length, 1, 1, res.length || 1);
  }

  public insertDeviceMapping(dto: Omit<DeviceMappingDTO, "id" | "plantRef">) {
    return this._ajaxController.post<typeof dto, DeviceMappingDTO>(
      `${this._telemetryDataEndpoint}/telemetry/device-mappings`,
      dto
    );
  }

  public updateDeviceMapping(id: number, dto: Omit<DeviceMappingDTO, "id" | "plantRef">) {
    return this._ajaxController.put<typeof dto, DeviceMappingDTO>(
      `${this._telemetryDataEndpoint}/telemetry/device-mappings/${id}`,
      dto
    );
  }

  public deleteDeviceMapping(id: number) {
    return this._ajaxController.delete(
      `${this._telemetryDataEndpoint}/telemetry/device-mappings/${id}`
    );
  }
}

export const telemetryAjax = new TelemetryAjax();
